import { CheckCircle2, Info } from "lucide-react";
import type {
  MyPortfolioClosedTradeReadModel,
  MyPortfolioNextReadModel,
  MyPortfolioSourcedMetric,
} from "../contracts.js";

export function StateScreen({ icon, title, text }: { icon: React.ReactNode; title: string; text: string }) {
  return <div className="portfolio-next-state"><div>{icon}<h2>{title}</h2><p>{text}</p></div></div>;
}

export function ScreenIntro({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return <div className="portfolio-x-screen-intro"><span>{eyebrow}</span><h2>{title}</h2><p>{description}</p></div>;
}

export function PanelHead({ title, subtitle }: { title: string; subtitle: string }) {
  return <div className="portfolio-x-panel-head"><div><b>{title}</b><small>{subtitle}</small></div></div>;
}

export function MetricCard({ label, metric, currency, suffix, tone, formatter }: {
  label: string; metric: MyPortfolioSourcedMetric<number>; currency?: string; suffix?: string;
  tone?: "positive" | "negative" | null; formatter?: (value: number) => string;
}) {
  const value = metric.value === null ? "غير متاح" : formatter ? formatter(metric.value)
    : currency ? moneyValue(metric.value, currency) : `${round(metric.value)}${suffix ?? ""}`;
  return <article className={tone ? `is-${tone}` : ""}><small>{label}</small><strong dir={currency ? "ltr" : undefined}>{value}</strong>
    <span className="portfolio-next-metric-meta"><code>{metric.state}</code>{metric.reasonCodes[0] ?? sourceNote(metric)}</span></article>;
}

export function MiniMetric({ label, metric, currency, text }: {
  label: string; metric?: MyPortfolioSourcedMetric<number>; currency?: string; text?: string;
}) {
  return <div className={toneOf(metric?.value ?? null) ? `is-${toneOf(metric?.value ?? null)}` : ""}><small>{label}</small>
    <b dir={currency ? "ltr" : undefined}>{text ?? (metric ? currency ? money(metric, currency) : plain(metric) : "غير متاح")}</b></div>;
}

export function Allocation({ model }: { model: MyPortfolioNextReadModel }) {
  const known = model.holdings.filter((holding) => holding.currentValue.value !== null);
  const total = known.reduce((sum, holding) => sum + holding.currentValue.value!, 0);
  const colors = ["#58d7cd", "#5d9eea", "#9a84f6", "#f1b563", "#ff7e78"];
  let cursor = 0;
  const stops = known.map((holding, index) => {
    const start = cursor;
    cursor += total > 0 ? holding.currentValue.value! / total * 100 : 0;
    return `${colors[index % colors.length]} ${start}% ${cursor}%`;
  });
  if (!known.length || total <= 0) return <div className="portfolio-next-empty-chart">لا توجد قيم سوقية متاحة للتوزيع.</div>;
  return <div className="portfolio-x-allocation-wrap"><div className="portfolio-x-allocation-donut"
    style={{ background: `conic-gradient(${stops.join(",")})` }}><span><b>{known.length}</b><small>مراكز</small></span></div>
    <div className="portfolio-x-allocation-legend">{known.map((holding, index) => <span key={holding.holdingId}>
      <i style={{ background: colors[index % colors.length] }} /><b>{holding.symbol}</b>
      <small>{round(holding.currentValue.value! / total * 100)}%</small></span>)}</div></div>;
}

export function TradeOutcome({ label, trade, currency, tone }: {
  label: string; trade: MyPortfolioClosedTradeReadModel | null; currency: string; tone: string;
}) {
  return <article className={tone}><span>{trade ? <CheckCircle2 /> : <Info />}</span><small>{label}</small>
    <strong dir="ltr">{trade ? money(trade.realizedPnl, currency) : "غير متاح"}</strong><b>{trade?.symbol ?? "—"}</b></article>;
}

export function Stock({ symbol, subtitle }: { symbol: string; subtitle: string }) {
  return <div className="portfolio-x-stock"><i>{symbol.slice(0, 4)}</i><span><b>{symbol}</b><small>{subtitle}</small></span></div>;
}

export function Result({ metric, pct, currency }: {
  metric: MyPortfolioSourcedMetric<number>; pct: MyPortfolioSourcedMetric<number>; currency: string;
}) {
  const tone = toneOf(metric.value);
  return <span className={`portfolio-x-result ${tone ? `is-${tone}` : ""}`}><b dir="ltr">{money(metric, currency)}</b>
    <small>{pct.value === null ? "النسبة غير متاحة" : `${round(pct.value)}%`}</small></span>;
}

export function Detail({ label, value }: { label: string; value: string }) {
  return <div className="portfolio-x-detail-item"><small>{label}</small><b>{value}</b></div>;
}

export function money(metric: MyPortfolioSourcedMetric<number>, currency: string): string {
  return metric.value === null ? "غير متاح" : moneyValue(metric.value, currency);
}

export function moneyValue(value: number, currency: string): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(value);
}

export function plain<T>(metric: MyPortfolioSourcedMetric<T>): string {
  if (metric.value === null) return "غير متاح";
  return typeof metric.value === "number" ? new Intl.NumberFormat("en-US", { maximumFractionDigits: 4 }).format(metric.value)
    : String(metric.value);
}

export function plainDate(metric: MyPortfolioSourcedMetric<string>): string {
  return metric.value ? dateTime(metric.value) : "غير متاح";
}

export function dateTime(value: string | null): string {
  if (!value) return "غير متاح";
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? new Intl.DateTimeFormat("ar-SA", { dateStyle: "medium", timeStyle: "medium",
    timeZone: "Asia/Riyadh" }).format(new Date(parsed)) : value;
}

export function duration(value: number | null): string {
  if (value === null) return "غير متاح";
  if (value < 60_000) return `${round(value / 1000)} ثانية`;
  if (value < 3_600_000) return `${round(value / 60_000)} دقيقة`;
  if (value < 86_400_000) return `${round(value / 3_600_000)} ساعة`;
  if (value < 30 * 86_400_000) return `${round(value / 86_400_000)} يوم`;
  return `${round(value / (30 * 86_400_000))} شهر`;
}

function sourceNote(metric: MyPortfolioSourcedMetric<unknown>): string {
  return metric.sourceIds.length ? `${metric.sourceIds.length} مصدر` : "لا مصدر مؤهل";
}

export function toneOf(value: number | null): "positive" | "negative" | null {
  return value === null || value === 0 ? null : value > 0 ? "positive" : "negative";
}

export function tradeById(model: MyPortfolioNextReadModel, tradeId: string | null): MyPortfolioClosedTradeReadModel | null {
  return tradeId ? model.tradeHistory.trades.find((trade) => trade.tradeId === tradeId) ?? null : null;
}

export function qualityLabel(value: MyPortfolioNextReadModel["dataQuality"]["state"]): string {
  return ({ READY: "المصادر جاهزة", READY_WITH_WARNINGS: "جاهزة مع تنبيهات", REVIEW_REQUIRED: "تحتاج مراجعة",
    BLOCKED: "الحزمة محجوبة" })[value];
}

export function sampleLabel(value: MyPortfolioNextReadModel["behavior"]["sampleState"]): string {
  return ({ INSUFFICIENT_DATA: "غير كافية", PROVISIONAL_TREND: "اتجاه أولي",
    RELATIVELY_STABLE_TREND: "اتجاه مستقر نسبيًا" })[value];
}

export function bytes(value: number): string { return value < 1024 ? `${value} B` : `${round(value / 1024)} KB`; }
export function round(value: number): string { return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value); }
